import React, { useState, useEffect, useRef, useMemo, useCallback, useId } from 'react';
import { ChevronDown, ChevronRight, Check, Info, Search, X, Trash2 } from 'lucide-react';
import { FABRIC_TEXTURES } from '../data/fabricTextures';

const TextureSelector = ({
  selectedTexture,
  onTextureChange,
  showTip,
  onTipToggle,
  className = '',
  dropdownMode = 'overlay', // "overlay" | "inline"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const uid = useId();
  const listId = `texture-listbox-${uid.replace(/:/g, '')}`;
  const searchFieldId = `texture-search-${uid.replace(/:/g, '')}`;

  const isInline = dropdownMode === 'inline';
  const showInfoButton = Boolean(showTip);

  const filteredCategories = useMemo(() => {
    if (!FABRIC_TEXTURES.fabricTextures) return [];

    if (!searchQuery.trim()) {
      return FABRIC_TEXTURES.fabricTextures;
    }

    const query = searchQuery.toLowerCase().trim();
    return FABRIC_TEXTURES.fabricTextures.filter((category) => {
      const categoryMatches = category.category.toLowerCase().includes(query);
      const textureMatches = category.types.some(
        (texture) =>
          texture.name.toLowerCase().includes(query) ||
          texture.description.toLowerCase().includes(query)
      );
      return categoryMatches || textureMatches;
    }).map((category) => {
      if (!category.category.toLowerCase().includes(query)) {
        return {
          ...category,
          types: category.types.filter(
            (texture) =>
              texture.name.toLowerCase().includes(query) ||
              texture.description.toLowerCase().includes(query)
          ),
        };
      }
      return category;
    });
  }, [searchQuery]);

  const flatSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const rows = [];
    filteredCategories.forEach((category) => {
      category.types.forEach((texture) => {
        rows.push({ category, texture });
      });
    });
    return rows;
  }, [filteredCategories, searchQuery]);

  const totalMatchCount = flatSearchResults.length;

  const calculateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return 'bottom';

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 200 && spaceAbove > spaceBelow + 100) {
      return 'top';
    }
    return 'bottom';
  }, []);

  const calculateDropdownHeight = useCallback(() => {
    if (!containerRef.current) return 420;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (dropdownPosition === 'top') {
      const availableHeight = rect.top - 20;
      return Math.min(availableHeight, 420);
    }
    const availableHeight = viewportHeight - rect.bottom - 20;
    return Math.min(availableHeight, 420);
  }, [dropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.texture-selector')) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setDropdownPosition(calculateDropdownPosition());
    }
  }, [isOpen, calculateDropdownPosition]);

  // Expand relevant categories when panel opens or search changes
  useEffect(() => {
    if (!isOpen) return;

    if (searchQuery.trim()) {
      setExpandedCats(new Set(filteredCategories.map((c) => c.category)));
      return;
    }

    const matchCat = selectedTexture
      ? filteredCategories.find((c) => c.types.some((t) => t.name === selectedTexture.name))
      : null;
    if (matchCat) {
      setExpandedCats(new Set([matchCat.category]));
    } else if (filteredCategories[0]) {
      setExpandedCats(new Set([filteredCategories[0].category]));
    }
  }, [isOpen, searchQuery, filteredCategories, selectedTexture]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const t = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(t);
    }
    return undefined;
  }, [isOpen]);

  const isTextureSelected = (texture) => selectedTexture && selectedTexture.name === texture.name;

  const handleTextureSelect = (category, texture) => {
    const selectedTextureData = {
      ...texture,
      category: category.category,
      categoryName: category.category,
    };

    onTextureChange(selectedTextureData);
    setIsOpen(false);
    setSearchQuery('');
  };

  const removeTexture = (e) => {
    e?.stopPropagation?.();
    onTextureChange(null);
  };

  const toggleCategory = (categoryName) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) next.delete(categoryName);
      else next.add(categoryName);
      return next;
    });
  };

  const dropdownHeight = calculateDropdownHeight();
  const listMaxHeight = Math.max(Math.min(dropdownHeight - (searchQuery.trim() ? 100 : 140), 320), 140);

  const getDropdownPositionClasses = () => {
    if (dropdownPosition === 'top') {
      return 'bottom-full mb-2';
    }
    return 'top-full mt-2';
  };

  const triggerShellClass =
    'flex min-h-[2.75rem] w-full min-w-0 items-stretch overflow-hidden rounded-xl border border-slate-200 bg-white text-sm shadow-sm transition hover:bg-slate-50/80 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-0 focus-within:border-indigo-500';

  const panelClasses = isInline
    ? 'mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 ring-1 ring-slate-100'
    : `absolute left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl z-[9999] overflow-hidden ring-1 ring-slate-100 ${getDropdownPositionClasses()}`;

  return (
    <div className={`relative texture-selector w-full ${className}`} ref={containerRef}>
      <div className="flex items-center gap-2">
        <div className={`flex min-w-0 flex-1 ${triggerShellClass}`}>
          <button
            ref={triggerRef}
            type="button"
            className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2 text-left text-sm outline-none"
            onClick={() => setIsOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listId}
          >
            {selectedTexture ? (
              <>
                <span className="truncate font-medium leading-tight text-slate-900">{selectedTexture.name}</span>
                <span className="mt-0.5 truncate text-xs text-slate-500">{selectedTexture.categoryName}</span>
              </>
            ) : (
              <span className="text-slate-500">Select fabric type…</span>
            )}
          </button>
          {isInline && selectedTexture && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                removeTexture(e);
              }}
              className="shrink-0 border-l border-slate-200 px-2.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              title="Clear fabric type"
              aria-label="Clear fabric type"
            >
              <X size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            className="flex shrink-0 items-center border-l border-slate-200 px-2.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label={isOpen ? 'Close fabric list' : 'Open fabric list'}
            tabIndex={-1}
          >
            <ChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showInfoButton && (
          <button
            type="button"
            onClick={onTipToggle}
            className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Fabric texture help"
          >
            <Info size={16} />
          </button>
        )}
      </div>

      {/* Full selected summary (non-inline): keeps parity for overlay / product forms */}
      {selectedTexture && !isInline && (
        <div className="mt-2 rounded-xl border border-indigo-100 bg-indigo-50/80 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Check size={14} className="shrink-0 text-indigo-600" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-indigo-950">{selectedTexture.name}</div>
                <div className="truncate text-xs text-indigo-700/90">{selectedTexture.categoryName}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={removeTexture}
              className="shrink-0 rounded-lg p-1.5 text-indigo-600 hover:bg-indigo-100"
              title="Remove texture"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className={panelClasses}
          style={{
            maxHeight: `${dropdownHeight}px`,
            minHeight: searchQuery.trim() ? '200px' : '220px',
            width: '100%',
          }}
        >
          <div
            className={`sticky top-0 z-20 border-b border-slate-100 bg-slate-50/95 px-3 py-3 backdrop-blur-sm ${searchQuery.trim() ? 'bg-indigo-50/90' : ''}`}
          >
            <label htmlFor={searchFieldId} className="sr-only">
              Search fabric types
            </label>
            <div className="relative">
              <Search
                size={16}
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${searchQuery.trim() ? 'text-indigo-500' : 'text-slate-400'}`}
              />
              <input
                id={searchFieldId}
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, category, or feel…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery('');
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>{selectedTexture ? '1 selected' : 'None selected'}</span>
              {searchQuery.trim() ? (
                <span className="font-medium text-indigo-700">
                  {totalMatchCount} match{totalMatchCount !== 1 ? 'es' : ''}
                </span>
              ) : null}
            </div>
          </div>

          <div
            id={listId}
            role="listbox"
            aria-label="Fabric types"
            className="overflow-y-auto overscroll-contain bg-white"
            style={{ maxHeight: `${listMaxHeight}px` }}
          >
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                {searchQuery.trim() ? 'No fabric types match your search.' : 'No fabric types available.'}
              </div>
            ) : searchQuery.trim() ? (
              <ul className="divide-y divide-slate-100 py-1">
                {flatSearchResults.map(({ category, texture }) => (
                  <li key={`${category.category}-${texture.name}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isTextureSelected(texture)}
                      onClick={() => handleTextureSelect(category, texture)}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition hover:bg-indigo-50/80 ${
                        isTextureSelected(texture) ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <span className="mt-0.5 shrink-0">
                        {isTextureSelected(texture) ? (
                          <Check size={18} className="text-indigo-600" />
                        ) : (
                          <span className="block h-[18px] w-[18px]" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="font-medium text-slate-900">{texture.name}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                            {category.category}
                          </span>
                        </span>
                        <span className="mt-1 line-clamp-2 text-xs leading-snug text-slate-600">{texture.description}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-1">
                {filteredCategories.map((category) => {
                  const expanded = expandedCats.has(category.category);
                  return (
                    <div key={category.category} className="border-b border-slate-100 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => toggleCategory(category.category)}
                        className="flex w-full items-center justify-between gap-2 bg-slate-50/90 px-3 py-2.5 text-left text-sm font-semibold text-slate-800 hover:bg-slate-100/90"
                        aria-expanded={expanded}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate">{category.category}</span>
                          <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200">
                            {category.types.length}
                          </span>
                        </span>
                        {expanded ? (
                          <ChevronDown size={16} className="shrink-0 text-slate-500" />
                        ) : (
                          <ChevronRight size={16} className="shrink-0 text-slate-500" />
                        )}
                      </button>

                      {expanded && (
                        <ul className="pb-1" role="group" aria-label={category.category}>
                          {category.types.map((texture) => (
                            <li key={texture.name}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isTextureSelected(texture)}
                                onClick={() => handleTextureSelect(category, texture)}
                                className={`flex w-full gap-3 border-t border-slate-50 px-4 py-2.5 text-left text-sm transition first:border-t-0 hover:bg-indigo-50/70 ${
                                  isTextureSelected(texture) ? 'bg-indigo-50' : ''
                                }`}
                              >
                                <span className="mt-0.5 shrink-0 pt-0.5">
                                  {isTextureSelected(texture) ? (
                                    <Check size={16} className="text-indigo-600" />
                                  ) : (
                                    <span className="block h-4 w-4 rounded border border-slate-200 bg-white" />
                                  )}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="font-medium text-slate-900">{texture.name}</span>
                                  <span className="mt-0.5 block text-xs leading-snug text-slate-600 line-clamp-2">
                                    {texture.description}
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showTip && (
        <div className="absolute right-0 top-12 z-10 max-w-xs rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg">
          <div className="mb-1 font-medium">Fabric texture selection</div>
          <div className="text-slate-300">
            Choose a fabric texture so generation can follow material behavior (drape, sheen, weight).
          </div>
        </div>
      )}
    </div>
  );
};

export default TextureSelector;
